/**
 * Rack list column width tokens — equal flex per Chapman design (#995 / #1154).
 *
 * Native compact (#999 Option B) is the ONLY layout that diverges from equal flex:
 * NAME + the descriptive middle column share equal flex (they absorb the free
 * space so NAME stops truncating) while the trailing metric column is pinned to a
 * fixed narrow width so short values (e.g. `—`, `Qty: 1`) don't float in a wide,
 * empty column. Web (desktop + mobile web) is untouched.
 */

import { isNativeCompactLayout } from '../../utils/locationTagListDisplay';

import type { RackTableColumnKey, RackTableLayout } from './rackListUtils';

/** Minimum width so uppercase headers stay on one line. */
const DESKTOP_EQUAL_MIN_WIDTH = 72;
const COMPACT_EQUAL_MIN_WIDTH = 48;

const DESKTOP_EQUAL_PADDING = 8;
const COMPACT_EQUAL_PADDING = 4;

/**
 * Native compact Option B skeleton (#999). NAME and the descriptive middle column
 * share the SAME flex so the first two columns are equal width and absorb the free
 * space; the trailing metric (and ROOM) is pinned to a fixed narrow width.
 */
const NATIVE_COMPACT_PRIMARY_FLEX = 1;
const NATIVE_COMPACT_TRAILING_WIDTH = 46;
const NATIVE_COMPACT_MIN_WIDTH = 48;

/** NAME + the descriptive middle column share equal flex on native compact. */
function isRackNativeCompactPrimaryColumn(column: RackTableColumnKey): boolean {
  return column === 'NAME' || column === 'PLATFORM';
}

export function rackColumnHorizontalPadding(
  _column: RackTableColumnKey,
  layout: RackTableLayout,
): number {
  return layout === 'compact' ? COMPACT_EQUAL_PADDING : DESKTOP_EQUAL_PADDING;
}

export function rackColumnCellStyle(
  column: RackTableColumnKey,
  layout: RackTableLayout,
): Record<string, unknown> {
  const paddingHorizontal = rackColumnHorizontalPadding(column, layout);
  // Desktop: keep STATUS compact. Compact/mobile: equal flex with other columns.
  if (column === 'STATUS' && layout !== 'compact') {
    return {
      flex: 0,
      flexGrow: 0,
      flexShrink: 0,
      width: 84,
      minWidth: 84,
      paddingHorizontal,
    };
  }
  // Native compact only (#999 Option B): NAME + middle share equal flex; every
  // other (trailing) column is fixed-narrow. Web paths fall through to the
  // equal-flex return below and stay byte-for-byte identical.
  if (isNativeCompactLayout(layout)) {
    if (isRackNativeCompactPrimaryColumn(column)) {
      return {
        flexGrow: NATIVE_COMPACT_PRIMARY_FLEX,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        paddingHorizontal,
      };
    }
    return {
      flex: 0,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: NATIVE_COMPACT_TRAILING_WIDTH,
      width: NATIVE_COMPACT_TRAILING_WIDTH,
      minWidth: NATIVE_COMPACT_TRAILING_WIDTH,
      maxWidth: NATIVE_COMPACT_TRAILING_WIDTH,
      paddingHorizontal,
    };
  }
  const minWidth =
    layout === 'compact' ? COMPACT_EQUAL_MIN_WIDTH : DESKTOP_EQUAL_MIN_WIDTH;
  return {
    flex: 1,
    flexBasis: 0,
    minWidth,
    paddingHorizontal,
  };
}

/** @internal — unit tests assert the native compact layout (#999). */
export function nativeCompactColumnLayoutForTests(): {
  primaryFlex: number;
  trailingWidth: number;
  minWidth: number;
} {
  return {
    primaryFlex: NATIVE_COMPACT_PRIMARY_FLEX,
    trailingWidth: NATIVE_COMPACT_TRAILING_WIDTH,
    minWidth: NATIVE_COMPACT_MIN_WIDTH,
  };
}

/** @internal — unit tests assert equal flex layout. */
export function desktopColumnLayoutForTests(): {
  equalFlex: number;
  equalMinWidth: number;
} {
  return {
    equalFlex: 1,
    equalMinWidth: DESKTOP_EQUAL_MIN_WIDTH,
  };
}

/** @internal — unit tests assert equal flex layout. */
export function compactColumnLayoutForTests(): {
  equalFlex: number;
  equalMinWidth: number;
} {
  return {
    equalFlex: 1,
    equalMinWidth: COMPACT_EQUAL_MIN_WIDTH,
  };
}
